/**
 * Custom hand-authored icon family for the GoobDoob VTT (redesign Phase 1).
 *
 * One 24×24 grid, 1.7 stroke, round caps/joins, inherits `currentColor` so it
 * themes for free. Replaces all emoji used as iconography. A few glyphs that
 * read better solid (inspiration, poison) fill `currentColor` instead of stroke.
 *
 * Usage:  <Icon name="sheet" size={20} />
 */
import type { ReactNode, SVGProps } from 'react';

export type IconName =
  | 'dashboard' | 'sheet' | 'map' | 'party' | 'lore' | 'world' | 'convergence' | 'audit' | 'admin'
  | 'select' | 'pen' | 'shape' | 'erase' | 'token' | 'measure' | 'fog' | 'text' | 'layers' | 'undo' | 'redo'
  | 'hp' | 'shield' | 'initiative' | 'inspiration' | 'poison' | 'death' | 'spell' | 'dice'
  | 'search' | 'settings' | 'close' | 'chevron' | 'eye' | 'eye-off' | 'upload' | 'add' | 'minus'
  | 'notify' | 'check' | 'warning' | 'coins' | 'menu' | 'levelup' | 'logout'
  | 'theme' | 'sun' | 'moon' | 'orbit';

const PATHS: Record<IconName, ReactNode> = {
  dashboard: <><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /></>,
  sheet: <><path d="M7 3h8l4 4v14H7z" /><path d="M15 3v4h4" /><path d="M10 12h6M10 16h6" /></>,
  map: <><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" /><path d="M9 4v14M15 6v14" /></>,
  party: <><circle cx="9" cy="10" r="5" /><circle cx="15" cy="14" r="5" /></>,
  lore: <><path d="M5 4h9a3 3 0 013 3v13H8a3 3 0 01-3-3z" /><path d="M8 8h7M8 12h5" /></>,
  world: <><circle cx="12" cy="12" r="8" /><path d="M4 12h16M12 4c3 3 3 13 0 16M12 4c-3 3-3 13 0 16" /></>,
  convergence: <><path d="M12 4l1.6 4L18 9.5l-4 1.6L12 16l-1.6-4.9L6 9.5l4.4-1.5z" /><circle cx="18.5" cy="6" r="1" /><circle cx="6" cy="17" r="1" /></>,
  audit: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
  admin: <><path d="M12 3l7 3v6c0 4-3 7-7 8-4-1-7-4-7-8V6z" /><circle cx="12" cy="11" r="2" /></>,
  select: <path d="M5 3l14 7-6 1.5-2.5 6z" />,
  pen: <><path d="M4 20l3-1 11-11-2-2L5 17z" /><path d="M14 6l2 2" /></>,
  shape: <rect x="5" y="5" width="14" height="14" rx="2" />,
  erase: <><path d="M6 15l5-5 7 7-3 3H9z" /><path d="M13 8l3-3 4 4-3 3" /></>,
  token: <><circle cx="12" cy="9" r="3" /><path d="M6 19a6 6 0 0112 0" /></>,
  measure: <><path d="M4 14L14 4l6 6L10 20z" /><path d="M8 12l1.5 1.5M11 9l1.5 1.5" /></>,
  fog: <path d="M7 16h9a3 3 0 000-6 4 4 0 00-7.7-1A3 3 0 007 16z" />,
  text: <path d="M6 6h12M12 6v12" />,
  layers: <><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></>,
  undo: <><path d="M9 7L5 11l4 4" /><path d="M5 11h9a4 4 0 010 8" /></>,
  redo: <><path d="M15 7l4 4-4 4" /><path d="M19 11h-9a4 4 0 000 8" /></>,
  hp: <path d="M12 20s-7-4.5-7-10a4 4 0 017-2.5A4 4 0 0119 10c0 5.5-7 10-7 10z" />,
  shield: <path d="M12 3l7 3v6c0 4-3 7-7 8-4-1-7-4-7-8V6z" />,
  initiative: <path d="M13 3L5 14h6l-2 7 8-11h-6z" />,
  inspiration: <path fill="currentColor" stroke="none" d="M12 3l2.5 6 6 .5-4.5 4 1.4 6L12 16.5 6.6 19.5 8 13.5 3.5 9.5 9.5 9z" />,
  poison: <path fill="currentColor" stroke="none" d="M12 3c4 5 6 8 6 11a6 6 0 01-12 0c0-3 2-6 6-11z" />,
  death: <><path d="M6 11a6 6 0 1112 0v3l-2 1v3h-3v-2h-2v2H8v-3l-2-1z" /><circle cx="9.5" cy="11" r="1.1" /><circle cx="14.5" cy="11" r="1.1" /></>,
  spell: <><path d="M12 4l1.6 4L18 9.5l-4 1.6L12 16l-1.6-4.9L6 9.5l4.4-1.5z" /><path d="M18 14l.7 1.8L20.5 16l-1.8.7L18 18.5l-.7-1.8L15.5 16z" /></>,
  dice: <><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><path d="M12 8l3 1.7v3.4L12 16l-3-1.9v-3.4z" /></>,
  coins: <><circle cx="9" cy="10" r="5" /><circle cx="15" cy="14" r="5" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M12 4v2M12 18v2M4 12h2M18 12h2M6 6l1.5 1.5M16.5 16.5L18 18M18 6l-1.5 1.5M7.5 16.5L6 18" /></>,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  chevron: <path d="M6 9l6 6 6-6" />,
  eye: <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
  'eye-off': <><path d="M4 4l16 16" /><path d="M2 12s4-7 10-7c2 0 3.7.7 5.2 1.6M22 12s-4 7-10 7c-2 0-3.7-.7-5.2-1.6" /></>,
  upload: <><path d="M12 16V5M7 10l5-5 5 5" /><path d="M4 19h16" /></>,
  add: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  notify: <><path d="M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8z" /><path d="M10 20a2 2 0 004 0" /></>,
  check: <path d="M5 12l5 5L20 6" />,
  warning: <><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17v.5" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  levelup: <><path d="M6 13l6-6 6 6" /><path d="M6 18l6-6 6 6" /></>,
  logout: <><path d="M14 7V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2h6a2 2 0 002-2v-2" /><path d="M10 12h11M18 9l3 3-3 3" /></>,
  theme: <><circle cx="12" cy="12" r="8" /><path d="M12 4a8 8 0 010 16z" fill="currentColor" stroke="none" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.5 5.5l1.5 1.5M17 17l1.5 1.5M18.5 5.5L17 7M7 17l-1.5 1.5" /></>,
  moon: <path d="M20 14a8 8 0 11-10-10 6 6 0 0010 10z" />,
  orbit: <><circle cx="12" cy="12" r="3" /><ellipse cx="12" cy="12" rx="9" ry="4" /><circle cx="21" cy="12" r="1.3" fill="currentColor" stroke="none" /></>,
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Pixel size for width & height. Default 20. */
  size?: number;
  /** Accessible label. When omitted the icon is decorative (aria-hidden). */
  title?: string;
}

export function Icon({ name, size = 20, title, className, ...rest }: IconProps) {
  return (
    <svg
      className={`ui-icon${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}

/** The full list of available icon names (handy for the showcase / tests). */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];
